// ============================================================================
// Canonical Broiler Dailys route contracts — the hub AND its daily record
// ============================================================================
//
// Both surfaces under BroilerDailysRouter are written in ONE module, for the
// reason `layerDailysPaths.ts` states: `{view: 'broilerdailys', paths:
// ['/broiler/dailys/*']}` is a single splat row, and the router picks between
// exactly two shapes on the RAW pathname alone:
//
//   /broiler/dailys        -> broiler.dailys.index   (BroilerDailysHub)
//   /broiler/dailys/       -> broiler.dailys.index   (empty suffix, hub again)
//   /broiler/dailys/<id>   -> broiler.dailys.detail  (PoultryDailyPage)
//
// The router's own expression is
// `pathname.startsWith('/broiler/dailys/') ? pathname.slice(len) || null : null`
// (BroilerDailysView.tsx), so the index predicate's correctness is a statement
// about what the record predicate does NOT claim, and vice versa. Splitting
// them across two modules would mean writing an exclusion in one file that only
// the other file's contents make true.
//
// Neither predicate knows about the ledger, the renderer, or any data owner.
//
// RESERVED SEGMENTS: there are NONE beneath `/broiler/dailys/`.
// `src/lib/routes.ts` registers exactly one `/broiler/dailys` path
// (`VIEW_TO_PATH.broilerdailys`) and no `/broiler/dailys/<segment>` route,
// alias source, alias target, or prefix alias — the broiler domain appears in
// neither ALIASES_EXACT nor ALIASES_PREFIX. BROILER_ROUTES declares no sibling
// namespace inside this splat either (unlike `/cattle/batches/forecast`), and
// the router's id expression special-cases no value. That absence is derived,
// not asserted here: a future `/broiler/dailys/archive` row would have to add a
// reserved set to this module AND appear in the static proof that pins the
// three facts above; nothing else would notice it.
//
// NOT a route fact, but the reason this pair is fenced rather than trusted:
// `showForm` puts `broiler.batchForm.fullscreen` AHEAD of the domain dispatch
// for every `/broiler/*` pathname except `/broiler/batches/*`, so an operator
// holding the full-screen batch form open still gets the form at these paths.
// That branch runs before ledger membership and is unaffected by either
// predicate here; it is named so a reader does not go looking for it.
// ============================================================================

import {isCanonicalCommentAnchorHash} from './commentAnchorHash.ts';

export const BROILER_DAILYS_INDEX_PATHNAME = '/broiler/dailys';
export const BROILER_DAILYS_DETAIL_PATHNAME_PREFIX = '/broiler/dailys/';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

// One meaningful raw segment: no separator in either direction, no NUL, no line
// break, and no query/fragment marker smuggled into a bare pathname string.
function isSafeRawSegment(rawSegment: string): boolean {
  return !(
    rawSegment === '' ||
    rawSegment.includes('/') ||
    rawSegment.includes('\\') ||
    rawSegment.includes('\0') ||
    rawSegment.includes('\r') ||
    rawSegment.includes('\n') ||
    rawSegment.includes('?') ||
    rawSegment.includes('#')
  );
}

// The same rules again after ONE decode, which is what turns `%2F`, `%5C`,
// `%00`, `%0A`, `%0D`, `%2E`, and `%2E%2E` into rejections rather than into
// accepted opaque text. A malformed escape makes decodeURIComponent throw and
// the caller's catch returns false.
function isSafeDecodedSegment(segment: string): boolean {
  return (
    segment.trim() !== '' &&
    segment !== '.' &&
    segment !== '..' &&
    !segment.includes('/') &&
    !segment.includes('\\') &&
    !segment.includes('\0') &&
    !segment.includes('\r') &&
    !segment.includes('\n') &&
    !segment.includes('?') &&
    !segment.includes('#')
  );
}

function pathnameOf(location: unknown): unknown {
  if (typeof location === 'string') return location;
  if (!location || typeof location !== 'object' || Array.isArray(location)) return null;
  return (location as LocationLike).pathname;
}

/**
 * The bare hub path, and nothing else.
 *
 * `/broiler/dailys/` is excluded deliberately: BroilerDailysRouter DOES render
 * the hub there (the suffix is empty, so `dailyId` is null), but the splat base
 * is not the audited direct-entry route, and the generic resolver classifies it
 * as this same index surface. Leaving it inside the release would hand a
 * RELEASED verdict to a raw shape no reviewer admitted; the disposition
 * boundary instead supplies explicit recovery.
 */
export function isCanonicalBroilerDailysIndexPathname(pathname: unknown): pathname is string {
  return pathname === BROILER_DAILYS_INDEX_PATHNAME;
}

/**
 * `/broiler/dailys/<id>` with exactly one decoded-safe segment.
 *
 * `/broiler/dailys` does not start with the prefix and `/broiler/dailys/` ends
 * at it, so this predicate cannot claim either hub form. The raw segment is
 * checked BEFORE the decoder so a literal separator, NUL, or line break never
 * reaches it, then the value is decoded once and re-checked so an encoded
 * separator, dot segment, NUL, or line break cannot widen the record release.
 */
export function isCanonicalBroilerDailysDetailPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(BROILER_DAILYS_DETAIL_PATHNAME_PREFIX)) {
    return false;
  }
  const rawSegment = pathname.slice(BROILER_DAILYS_DETAIL_PATHNAME_PREFIX.length);
  if (!isSafeRawSegment(rawSegment)) return false;
  try {
    return isSafeDecodedSegment(decodeURIComponent(rawSegment));
  } catch {
    return false;
  }
}

/**
 * The hub interprets NEITHER search nor hash. `BroilerDailysHub` calls no
 * location hook at all — the router reads `location.pathname` and hands the hub
 * the batch directory — and its filter, group and saved-view state is component
 * or persisted state, never URL state. So a presentation-only query or fragment
 * on the canonical hub path does not change this claim, exactly as on the
 * released Cattle and Layer Dailys indexes.
 */
export function isCanonicalBroilerDailysIndexLocation(location: unknown): boolean {
  return isCanonicalBroilerDailysIndexPathname(pathnameOf(location));
}

/**
 * The record page is the opposite case, and the difference is real rather than
 * stylistic: `PoultryDailyPage` READS `location.hash` to scroll a deep-linked
 * `#comment-<id>` mention into view. A hash-carrying load is therefore a
 * materially different render than the audited one, so both search and hash
 * stay out of the released pair and receive explicit route recovery rather
 * than a broad loader fallback.
 *
 * A bare string is accepted only when it is plainly a pathname, so a caller or
 * test seam cannot smuggle an unsupported query or fragment into the target.
 */
export function isCanonicalBroilerDailysDetailLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalBroilerDailysDetailPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '') &&
    (hash === undefined || hash === '' || isCanonicalCommentAnchorHash(hash)) &&
    isCanonicalBroilerDailysDetailPathname(pathname)
  );
}
